import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { Checkbox, Switch, Select, InputNumber, Button, Modal } from 'antd';
import { DownOutlined, UpOutlined, ClockCircleOutlined, ExpandOutlined } from '@ant-design/icons';
import AIRichText from 'components/AIRichText';
import AiTextEditor from 'components/AI/AiTextEditor';
import AttachFiles from './components/AttachFiles';
import { EAIEditorType, EAIRichTextEditorType } from 'types/ai';
import s from './s.module.less';
import { FileUploadResult } from 'hooks/useFileUpload';
import { getAiTreatmentPlan } from 'api/ai';
import { autoSaveInfo, saveAttachment } from 'api/treatment';
import { TextVisitCase, TextVisitServiceType } from 'types/textVisit';
import PrescribeMedication from './components/PrescribeMedication';
import { useAutoSave, SaveStatus } from 'hooks/useAutoSave';
import useVersionHistory from 'hooks/treatment/useVersionHistory';
import { IVideoTreatmentDetail, EAutoSaveBusinessType, EBusinessType } from 'types/treatment';
import { getProviderNameDisplay } from 'utils/provider';
import ProviderStore from 'store/Provider';
import VersionHistoryDrawer from './components/VersionHistoryDrawer';
import VisitCard from './components/VisitCard';
import { TServerCalendarAppointmentEventDetail } from 'types/calendar';
import FollowUpModal from 'components/FollowUpModal';
import RescheduleModal from 'components/RescheduleModal';
import { EFeatureType } from 'types/provider';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import TreatmentTemplateSelection from './components/TreatmentTemplateSelection';
import { getTreatmentTemplateById } from 'api/videoSetting';
import BillingNote from './components/BillingNote';
import DiagnosisSection from './components/DiagnosisSection';
import BillingCodesSection from './components/BillingCodesSection';
import { TVisitDiagnosisEntry, TVisitBillingCodeEntry } from 'types/insuranceBilling';
import useTreatmentPlanInfo from 'hooks/useTreatmentPlanInfo';

type Props = {
    businessId: number; // textVisitId 或 appointmentId
    caseData?: TextVisitCase | IVideoTreatmentDetail;
    refetch?: (businessId: number | string) => void; // textVisitUuid 是 string，appointmentId 是 number
    type?: 'text' | 'video' | 'standalone';
    showUpcomingVisits?: boolean;
    hidePrescriptions?: boolean;
    hideTreatmentPlan?: boolean;
    hideNotes?: boolean;
    // 会中（MeetingPage）内联操作按钮，不传则不渲染，其他使用方不受影响
    onSave?: () => void;
    onSignAndSend?: () => void;
    saving?: boolean;
    // visit notes 已 Sign & Send 提交：Visit Notes 区块的 Save / Sign and send 置灰
    visitNotesSubmitted?: boolean;
    // 模板一次性引导气泡，仅会中（MeetingPage）开启
    enableTemplateGuide?: boolean;
};

// 保存状态显示组件
const SaveStatusIndicator: React.FC<{
    status?: SaveStatus;
    hasEverSaved: boolean;
    onHistoryClick: () => void;
}> = ({ status, hasEverSaved, onHistoryClick }) => {
    return (
        <div className={s.saveIndicator}>
            <span className={s.statusSlot}>
                {status === 'saving' && (
                    <span className={s.saveStatus}>
                        <ClockCircleOutlined spin style={{ color: '#111' }} />
                        <span>Saving...</span>
                    </span>
                )}
                {status === 'saved' && (
                    <span className={s.saveStatus}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path
                                d="M1.33398 9.66687C1.33398 10.0608 1.41158 10.4509 1.56235 10.8149C1.71311 11.1789 1.93409 11.5096 2.21266 11.7882C2.77527 12.3508 3.53834 12.6669 4.33398 12.6669H12.334C12.9246 12.6679 13.4937 12.445 13.9264 12.0429C14.3591 11.6409 14.6232 11.0897 14.6655 10.5006C14.7078 9.91149 14.5251 9.32825 14.1543 8.86854C13.7834 8.40882 13.2521 8.10686 12.6673 8.02354C12.6738 6.88938 12.2671 5.79167 11.5231 4.9356C10.7791 4.07953 9.74885 3.5237 8.62487 3.37202C7.50088 3.22033 6.36012 3.48316 5.41583 4.11139C4.47154 4.73962 3.78836 5.69023 3.49398 6.78554C2.87062 6.96736 2.32307 7.34652 1.93358 7.86607C1.5441 8.38563 1.3337 9.01754 1.33398 9.66687Z"
                                stroke="#4B5563"
                                strokeWidth="1.5"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M10 7.33398L7.33333 10.0007L6 8.66732"
                                stroke="#4B5563"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <span>Saved</span>
                    </span>
                )}
            </span>
            {hasEverSaved && (
                <span className={`${s.saveStatus} ${s.historyStatus}`} onClick={onHistoryClick}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path
                            d="M6 12C4.6 12 3.36111 11.5751 2.28333 10.7253C1.20556 9.87556 0.505556 8.78933 0.183333 7.46667C0.138889 7.3 0.172222 7.14733 0.283333 7.00867C0.394444 6.87 0.544444 6.78933 0.733333 6.76667C0.911111 6.74444 1.07222 6.77778 1.21667 6.86667C1.36111 6.95556 1.46111 7.08889 1.51667 7.26667C1.78333 8.26667 2.33333 9.08333 3.16667 9.71667C4 10.35 4.94444 10.6667 6 10.6667C7.3 10.6667 8.40289 10.214 9.30867 9.30867C10.2144 8.40333 10.6671 7.30044 10.6667 6C10.6662 4.69956 10.2136 3.59689 9.30867 2.692C8.40378 1.78711 7.30089 1.33422 6 1.33333C5.23333 1.33333 4.51667 1.51111 3.85 1.86667C3.18333 2.22222 2.62222 2.71111 2.16667 3.33333H3.33333C3.52222 3.33333 3.68067 3.39733 3.80867 3.52533C3.93667 3.65333 4.00044 3.81156 4 4C3.99956 4.18844 3.93556 4.34689 3.808 4.47533C3.68044 4.60378 3.52222 4.66756 3.33333 4.66667H0.666667C0.477778 4.66667 0.319556 4.60267 0.192 4.47467C0.0644445 4.34667 0.000444444 4.18844 0 4V1.33333C0 1.14444 0.0640001 0.986222 0.192 0.858667C0.32 0.731111 0.478222 0.667111 0.666667 0.666667C0.855111 0.666222 1.01356 0.730222 1.142 0.858667C1.27044 0.987111 1.33422 1.14533 1.33333 1.33333V2.23333C1.9 1.52222 2.59178 0.972222 3.40867 0.583333C4.22556 0.194444 5.08933 0 6 0C6.83333 0 7.614 0.158444 8.342 0.475333C9.07 0.792222 9.70333 1.21978 10.242 1.758C10.7807 2.29622 11.2084 2.92956 11.5253 3.658C11.8422 4.38645 12.0004 5.16711 12 6C11.9996 6.83289 11.8413 7.61356 11.5253 8.342C11.2093 9.07044 10.7816 9.70378 10.242 10.242C9.70244 10.7802 9.06911 11.208 8.342 11.5253C7.61489 11.8427 6.83422 12.0009 6 12ZM6.66667 5.73333L8.33333 7.4C8.45555 7.52222 8.51667 7.67778 8.51667 7.86667C8.51667 8.05556 8.45555 8.21111 8.33333 8.33333C8.21111 8.45556 8.05556 8.51667 7.86667 8.51667C7.67778 8.51667 7.52222 8.45556 7.4 8.33333L5.53333 6.46667C5.46667 6.4 5.41667 6.32511 5.38333 6.242C5.35 6.15889 5.33333 6.07267 5.33333 5.98333V3.33333C5.33333 3.14444 5.39733 2.98622 5.52533 2.85867C5.65333 2.73111 5.81156 2.66711 6 2.66667C6.18844 2.66622 6.34689 2.73022 6.47533 2.85867C6.60378 2.98711 6.66756 3.14533 6.66667 3.33333V5.73333Z"
                            fill="#4B5563"
                        />
                    </svg>
                    History
                </span>
            )}
        </div>
    );
};

const Prescriptions = forwardRef((props: Props, ref) => {
    const {
        businessId,
        caseData,
        refetch,
        type = 'text-visit',
        showUpcomingVisits = false,
        hidePrescriptions = false,
        hideTreatmentPlan = false,
        hideNotes = false,
        onSave,
        onSignAndSend,
        saving = false,
        visitNotesSubmitted = false,
        enableTemplateGuide = false,
    } = props;

    const treatmentPlanSectionRef = useRef<HTMLDivElement>(null);

    const [autoFollowUpEnabled, setAutoFollowUpEnabled] = useState(true);
    const [isTreatmentPlanAiDraft, setIsTreatmentPlanAiDraft] = useState(false);
    // 回显已保存的 AI-Assisted Draft 开关时置 true，跳过一次 AI 生成，避免覆盖已保存的 Visit Notes
    const skipAiDraftGenerateRef = useRef(false);
    // 每个 business 只回显一次：初始化 effect 会因 homeInfo/prefillData 异步到达或 caseData 刷新而重跑，
    // 不能把用户会中手动打开的开关重置回接口保存值
    const aiDraftHydratedKeyRef = useRef<number>();
    const [showNotes, setShowNotes] = useState(false);
    const [treatmentPlanValue, setTreatmentPlanValue] = useState('');
    const [myNotes, setMyNotes] = useState('');
    const [attachFiles, setAttachFiles] = useState<FileUploadResult[]>([]);
    const [followUpNumber, setFollowUpNumber] = useState(21);
    const [followUpUnit, setFollowUpUnit] = useState<'D' | 'W' | 'M'>('D');
    const [followUpMessage, setFollowUpMessage] = useState('');
    const [aPlanLoading, setAPlanLoading] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [versionHistoryBusinessType, setVersionHistoryBusinessType] = useState<EAutoSaveBusinessType | undefined>(
        undefined,
    );
    const [displayUpcomingVisits, setDisplayUpcomingVisits] = useState<TServerCalendarAppointmentEventDetail[]>([]);
    const [showFollowUpModal, setShowFollowUpModal] = useState(false);
    const [showRescheduleModal, setShowRescheduleModal] = useState(false);
    const [scheduledVisit, setScheduledVisit] = useState<TServerCalendarAppointmentEventDetail>();
    const [isTreatmentPlanFullScreenOpen, setIsTreatmentPlanFullScreenOpen] = useState(false);
    const [isProviderNoteFullScreenOpen, setIsProviderNoteFullScreenOpen] = useState(false);
    // 模板选中状态提升到父级：内联面板与全屏弹窗的 TreatmentTemplateSelection 共享
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | undefined>();
    const [billingNote, setBillingNote] = useState('');
    const [diagnosisEntries, setDiagnosisEntries] = useState<TVisitDiagnosisEntry[]>([]);
    const [billingCodeEntries, setBillingCodeEntries] = useState<TVisitBillingCodeEntry[]>([]);
    // 记录点击的是哪个区块的 Save，loading 只显示在被点击的按钮上
    const [savingSection, setSavingSection] = useState<
        'visitNotes' | 'myNotes' | 'billingNote' | 'diagnosis' | 'billingCodes'
    >();

    useEffect(() => {
        if (!saving) {
            setSavingSection(undefined);
        }
    }, [saving]);

    // 会中（onSave 存在）时渲染在各卡片边框内底部的 Save 按钮，交互与 Visit Notes / My own notes 区一致
    const renderSectionSaveFooter = (section: 'billingNote' | 'diagnosis' | 'billingCodes') =>
        onSave ? (
            <div className={s.inlineActions}>
                <Button
                    type="primary"
                    onClick={() => {
                        setSavingSection(section);
                        onSave();
                    }}
                    loading={saving && savingSection === section}
                >
                    Save
                </Button>
            </div>
        ) : undefined;

    const [homeInfo] = useProviderHomeInfo();
    const inInMeetingWhitelist = true;
    const isRefill =
        caseData?.serviceType === TextVisitServiceType.MEDICATION_REFILL ||
        caseData?.serviceType === TextVisitServiceType.PRESCRIPTION_REFILL;

    const isTextVisit = type === 'text';
    const isStandalone = type === 'standalone';
    const [getUser] = ProviderStore.useStore();
    const providerName = getProviderNameDisplay(getUser('data'));

    const getPrefillBusinessType = () => {
        if (isStandalone) return 'STANDALONE';
        if (isTextVisit) return 'TEXT_VISIT';
        return 'APPOINTMENT';
    };
    const getAutoSaveTreatmentPlanType = () => {
        if (isStandalone) return EAutoSaveBusinessType.STANDALONE_TREATMENT_PLAN;
        if (isTextVisit) return EAutoSaveBusinessType.TEXT_VISIT_TREATMENT_PLAN;
        return EAutoSaveBusinessType.APPOINTMENT_TREATMENT_PLAN;
    };
    const getAutoSaveNotesType = () => {
        if (isStandalone) return EAutoSaveBusinessType.STANDALONE_NOTES;
        if (isTextVisit) return EAutoSaveBusinessType.TEXT_VISIT_NOTES;
        return EAutoSaveBusinessType.APPOINTMENT_NOTES;
    };
    const getAttachmentBusinessType = () => {
        if (isStandalone) return EBusinessType.STANDALONE;
        if (isTextVisit) return EBusinessType.TEXT_VISIT;
        return EBusinessType.APPOINTMENT;
    };
    const prefillBusinessType = getPrefillBusinessType();
    const autoSaveTreatmentPlanType = getAutoSaveTreatmentPlanType();
    const autoSaveNotesType = getAutoSaveNotesType();
    const attachmentBusinessType = getAttachmentBusinessType();
    const aiBusinessType = getAttachmentBusinessType();

    const [prefillData] = useTreatmentPlanInfo(
        prefillBusinessType,
        businessId,
        caseData?.providerId,
    );
    const [treatmentPlanHistoryData] = useVersionHistory({
        businessId,
        businessType: autoSaveTreatmentPlanType,
        autoFetch: true,
    });
    const [providerNoteHistoryData] = useVersionHistory({
        businessId,
        businessType: autoSaveNotesType,
        autoFetch: true,
    });

    const autoSaveTreatmentPlan = async (type: EAutoSaveBusinessType, val: string) => {
        try {
            return await autoSaveInfo({
                autoSaveBusinessType: type,
                businessId,
                content: val,
                id: caseData?.treatmentPlan?.id,
            });
        } catch (error) {
            console.error(error);
        }
    };

    const treatmentPlanAutoSave = useAutoSave({
        saveFn: async (val: string) =>
            autoSaveTreatmentPlan(autoSaveTreatmentPlanType, val),
    });

    const providerNoteAutoSave = useAutoSave({
        saveFn: async (val: string) =>
            autoSaveTreatmentPlan(autoSaveNotesType, val),
    });

    useEffect(() => {
        const defaultFollowUpMessage = `Hi ${caseData?.patientFirstName},\n\nIt\'s been a few weeks since your last visit, and I wanted to follow up to see how you\'re doing. If you have any questions, updates, or concerns, please don\'t hesitate to reach out through your patient portal.\n\nTake care,\n${providerName}`;
        const initialTreatmentPlan = caseData?.treatmentPlan?.treatmentPlan || '';
        const initialProviderNote = caseData?.treatmentPlan?.providerNote || '';

        // Only overwrite treatmentPlanValue if caseData has existing content,
        // to avoid overwriting template-filled content for new prescriptions
        if (initialTreatmentPlan) {
            setTreatmentPlanValue(initialTreatmentPlan);
        }
        setMyNotes(initialProviderNote);
        let autoEnabled: boolean = caseData?.treatmentPlan?.autoFollowUp || true;
        const defaultUnCheckSendAutoFollowUp = homeInfo?.features?.includes(EFeatureType.VISIT_FOLLOW_UP_REMINDER_OFF);
        if (defaultUnCheckSendAutoFollowUp) {
            autoEnabled = false;
        }
        setAutoFollowUpEnabled(autoEnabled);
        if (caseData && aiDraftHydratedKeyRef.current !== businessId) {
            aiDraftHydratedKeyRef.current = businessId;
            const savedAiDraft = !!caseData.treatmentPlan?.aiTreatmentDraftStatus;
            setIsTreatmentPlanAiDraft((prev) => {
                if (savedAiDraft && !prev) {
                    skipAiDraftGenerateRef.current = true;
                }
                return savedAiDraft;
            });
        }
        setFollowUpNumber(caseData?.treatmentPlan?.autoFollowUpInterval || 21);
        setFollowUpUnit(caseData?.treatmentPlan?.autoFollowUpIntervalUnit || 'D');
        setFollowUpMessage(caseData?.treatmentPlan?.autoFollowUpMessage || defaultFollowUpMessage);
        setAttachFiles(
            caseData?.treatmentPlan?.attachments?.map((attachment) => ({
                url: attachment.path || '',
                name: attachment.name,
                type: attachment.type,
            })) || [],
        );

        const tp = caseData?.treatmentPlan;
        setBillingNote(tp?.klarityBillingNote || '');
        const hasSavedDiagnoses = tp?.diagnoses && tp.diagnoses.length > 0;
        const hasSavedBillingCodes = tp?.billingCodes && tp.billingCodes.length > 0;
        if (hasSavedDiagnoses) {
            setDiagnosisEntries(
                tp!.diagnoses!.map((d, i) => ({
                    id: i + 1,
                    code: d.diagnosisCode,
                    description: d.description || '',
                    isPrimary: !!d.isPrimary,
                })),
            );
        }
        if (hasSavedBillingCodes) {
            setBillingCodeEntries(
                tp!.billingCodes!.map((b, i) => ({
                    id: i + 1,
                    cptCode: b.cptCode,
                    description: b.description || '',
                    unitBasis: b.unitBasis || 'PER_SESSION',
                    units: b.units || 1,
                    insuranceRate: b.insuranceRate,
                    isPrimary: !!b.isPrimary,
                })),
            );
        }
        if (!hasSavedDiagnoses) {
            setDiagnosisEntries(
                prefillData?.diagnosisHistory?.length
                    ? prefillData.diagnosisHistory.map((d, i) => ({
                          id: i + 1,
                          code: d.diagnosisCode,
                          description: d.description || '',
                          isPrimary: i === 0,
                          lastUsedAt: d.lastUsedAt || undefined,
                      }))
                    : [],
            );
        }
        if (!hasSavedBillingCodes) {
            let _prefillBillingCode: TVisitBillingCodeEntry[] = [];
            if (prefillData && prefillData.billingCodeHistory && prefillData.billingCodeHistory.length > 0) {
                _prefillBillingCode = prefillData.billingCodeHistory.map((b, i) => ({
                    id: i + 1,
                    cptCode: b.cptCode,
                    description: b.description || '',
                    unitBasis: b.unitBasis || 'PER_SESSION',
                    units: b.units || 1,
                    insuranceRate: b.insuranceRate ?? undefined,
                    isPrimary: i === 0,
                    lastUsedAt: b.lastUsedAt || undefined,
                }));
            } else if (prefillData && prefillData.templateCpts && prefillData.templateCpts.length > 0) {
                _prefillBillingCode = prefillData.templateCpts.map((t, i) => ({
                    id: i + 1,
                    cptCode: t.cptCode,
                    description: t.description || '',
                    unitBasis: 'PER_SESSION',
                    units: 1,
                    insuranceRate: t.insuranceRate ?? undefined,
                    isPrimary: i === 0,
                    lastUsedAt: undefined,
                }));
            }
            setBillingCodeEntries(_prefillBillingCode);
        }

        if (showUpcomingVisits) {
            setDisplayUpcomingVisits((caseData as IVideoTreatmentDetail)?.upcomingVisitAppointments || []);
        }

        // 初始化自动保存的基准值
        if (initialTreatmentPlan) {
            treatmentPlanAutoSave.setInitialValue(initialTreatmentPlan);
        }
        providerNoteAutoSave.setInitialValue(initialProviderNote);
    }, [caseData, homeInfo, prefillData]);

    useEffect(() => {
        const fetchAiTreatmentPlan = async () => {
            try {
                setAPlanLoading(true);
                const res = await getAiTreatmentPlan(
                    businessId,
                    aiBusinessType,
                    treatmentPlanValue,
                );
                if (!res.error) {
                    setTreatmentPlanValue(res.data.data);
                    treatmentPlanAutoSave.triggerAutoSave(res.data.data);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setAPlanLoading(false);
            }
        };
        if (isTreatmentPlanAiDraft) {
            if (skipAiDraftGenerateRef.current) {
                skipAiDraftGenerateRef.current = false;
                return;
            }
            fetchAiTreatmentPlan();
        }
    }, [isTreatmentPlanAiDraft]);

    useImperativeHandle(
        ref,
        () => ({
            getValues: () => ({
                treatmentPlan: treatmentPlanValue,
                providerNote: myNotes,
                aiTreatmentDraftStatus: isTreatmentPlanAiDraft,
                autoFollowUp: autoFollowUpEnabled,
                autoFollowUpMessage: followUpMessage,
                autoFollowUpInterval: followUpNumber,
                autoFollowUpIntervalUnit: followUpUnit,
                attachments: attachFiles.map((file) => ({
                    name: file.name,
                    path: file.url,
                    type: file.type,
                    previewUrl: file.previewUrl,
                })),
                klarityBillingNote: billingNote || undefined,
                diagnoses: diagnosisEntries
                    .filter((e) => e.code)
                    .map((e, i) => ({
                        diagnosisCode: e.code!,
                        description: e.description || undefined,
                        isPrimary: e.isPrimary,
                        seq: i,
                    })),
                billingCodes: billingCodeEntries
                    .filter((e) => e.cptCode)
                    .map((e, i) => ({
                        cptCode: e.cptCode!,
                        description: e.description || undefined,
                        isPrimary: e.isPrimary,
                        isSelected: true,
                        unitBasis: e.unitBasis,
                        units: e.units,
                        insuranceRate: e.insuranceRate,
                        seq: i,
                    })),
            }),
            getTreatmentPlanSectionRef: () => treatmentPlanSectionRef.current,
        }),
        [
            treatmentPlanValue,
            myNotes,
            isTreatmentPlanAiDraft,
            autoFollowUpEnabled,
            followUpMessage,
            followUpNumber,
            followUpUnit,
            attachFiles,
            billingNote,
            diagnosisEntries,
            billingCodeEntries,
        ],
    );

    const handleSaveAttachments = async (files: FileUploadResult[]) => {
        setAttachFiles(files);
        try {
            await saveAttachment(
                files.map((file) => ({
                    businessType: attachmentBusinessType,
                    businessId,
                    name: file.name,
                    path: file.url || '',
                    type: file.type,
                })),
            );
        } catch (error) {
            console.error(error);
        }
    };

    const handleApplyTemplate = async (templateId: number) => {
        try {
            const res = await getTreatmentTemplateById(templateId);
            if (!res.error && res.data?.data?.content) {
                setSelectedTemplateId(templateId);
                setTreatmentPlanValue(res.data.data.content);
                treatmentPlanAutoSave.triggerAutoSave(res.data.data.content);
            }
        } catch (error) {
            console.error('Failed to apply template:', error);
        }
    };

    return (
        <div className={s.prescriptions}>
            {!hidePrescriptions && (
                <div className={s.section}>
                    <PrescribeMedication caseData={caseData} refetch={refetch} />
                </div>
            )}

            <div className={s.section} ref={treatmentPlanSectionRef}>
                {!hideTreatmentPlan && (
                    <>
                        <div className={s.sectionHeader}>
                            <h3 className={s.sectionTitle}>Visit Notes (Patient-facing)</h3>
                            <p className={s.sectionSubtitle}>
                                Your input below will be shared with the patient directly
                            </p>
                        </div>
                        <div className={s.sectionContent}>
                            <div className={s.diagnosisHeader}>
                                <div className={s.diagnosisTitleRow}>
                                    <span className={s.title}>Diagnosis & Visit Notes</span>
                                    <SaveStatusIndicator
                                        status={treatmentPlanAutoSave.saveStatus}
                                        hasEverSaved={
                                            treatmentPlanAutoSave.hasEverSaved || treatmentPlanHistoryData?.length > 0
                                        }
                                        onHistoryClick={() => {
                                            setVersionHistoryBusinessType(autoSaveTreatmentPlanType);
                                            setShowHistory(true);
                                        }}
                                    />
                                </div>
                                {/* {(isTextVisit || isRefill) && ( */}
                                {true && (
                                    <div className={s.aiDraftBadge}>
                                        <span className={s.aiIcon}>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M7.47969 11.4796L9.02563 6.8418H9.97431L11.5203 11.4796L16.1581 13.0256V13.9743L11.5203 15.5202L9.97431 20.158H9.02563L7.47969 15.5202L2.84186 13.9743V13.0256L7.47969 11.4796Z"
                                                    fill="url(#paint0_linear_507_21172)"
                                                />
                                                <path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M16.2297 5.22963L17.0256 2.8418H17.9743L18.7703 5.22963L21.1581 6.02557V6.97425L18.7703 7.7702L17.9743 10.158H17.0256L16.2297 7.7702L13.8419 6.97425V6.02557L16.2297 5.22963Z"
                                                    fill="url(#paint1_linear_507_21172)"
                                                />
                                                <defs>
                                                    <linearGradient
                                                        id="paint0_linear_507_21172"
                                                        x1="12"
                                                        y1="2.8418"
                                                        x2="12"
                                                        y2="20.158"
                                                        gradientUnits="userSpaceOnUse"
                                                    >
                                                        <stop stopColor="#0E62F4" />
                                                        <stop offset="1" stopColor="#BB5BFF" />
                                                    </linearGradient>
                                                    <linearGradient
                                                        id="paint1_linear_507_21172"
                                                        x1="12"
                                                        y1="2.8418"
                                                        x2="12"
                                                        y2="20.158"
                                                        gradientUnits="userSpaceOnUse"
                                                    >
                                                        <stop stopColor="#0E62F4" />
                                                        <stop offset="1" stopColor="#BB5BFF" />
                                                    </linearGradient>
                                                </defs>
                                            </svg>
                                        </span>
                                        <span>AI-Assisted Draft</span>
                                        <Switch
                                            checked={isTreatmentPlanAiDraft}
                                            onChange={setIsTreatmentPlanAiDraft}
                                            disabled={visitNotesSubmitted}
                                        />
                                    </div>
                                )}
                                <Button
                                    type="text"
                                    icon={<ExpandOutlined />}
                                    onClick={() => setIsTreatmentPlanFullScreenOpen(true)}
                                    className={s.fullScreenBtn}
                                    aria-label="Expand visit notes editor to full screen"
                                />
                            </div>

                            <TreatmentTemplateSelection
                                specialty={
                                    isTextVisit || isStandalone
                                        ? (caseData as TextVisitCase)?.specialty
                                        : (caseData as IVideoTreatmentDetail)?.serviceSpecialty
                                }
                                visitType={(caseData as IVideoTreatmentDetail)?.serviceType}
                                isStandalone={isStandalone}
                                needInitContent={!caseData?.treatmentPlan?.treatmentPlan}
                                value={selectedTemplateId}
                                onChange={handleApplyTemplate}
                                disabled={visitNotesSubmitted}
                                enableGuide={enableTemplateGuide}
                            />
                            {/* 全屏弹窗打开时卸载内联编辑器：两个 Quill 实例同时绑定同一 value 会互相回写，导致弹窗内光标漂移 */}
                            {isTreatmentPlanFullScreenOpen && (
                                <div className={s.editorPlaceholder}>Editing in expanded view...</div>
                            )}
                            {!isTreatmentPlanFullScreenOpen && (
                                <AIRichText
                                    loading={aPlanLoading}
                                    value={treatmentPlanValue}
                                    onChange={(val) => {
                                        setTreatmentPlanValue(val);
                                        treatmentPlanAutoSave.triggerAutoSave(val);
                                    }}
                                    relationId={businessId}
                                    placeholder="Include your clinical assessment, diagnosis, and recommended visit notes..."
                                    type={
                                        isTextVisit || isStandalone
                                            ? EAIRichTextEditorType.TEXT_VISIT_TREATMENT
                                            : EAIRichTextEditorType.AI_EDITOR_APPOINTMENT_TREATMENT
                                    }
                                    disabled={visitNotesSubmitted}
                                />
                            )}
                            <AttachFiles
                                value={attachFiles}
                                onFilesChange={handleSaveAttachments}
                                accept="image/*,application/pdf"
                            />
                            {(onSave || onSignAndSend) && (
                                <div className={s.inlineActions}>
                                    {onSave && (
                                        <Button
                                            type="primary"
                                            onClick={() => {
                                                setSavingSection('visitNotes');
                                                onSave();
                                            }}
                                            disabled={visitNotesSubmitted}
                                            loading={saving && savingSection === 'visitNotes'}
                                        >
                                            Save
                                        </Button>
                                    )}
                                    {onSignAndSend && (
                                        <Button onClick={onSignAndSend} disabled={visitNotesSubmitted}>
                                            Sign and send
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}
                {!hideNotes && (
                    <div className={s.sectionContent}>
                        <div className={s.notesSectionHeader}>
                            <div className={s.notesTitleRow}>
                                <h4 className={s.title}>
                                    <p>My own notes</p>
                                    <p className={s.subTitle}>
                                        Include your own notes below. This part will NOT be shared with the patient.
                                    </p>
                                </h4>
                                <SaveStatusIndicator
                                    status={providerNoteAutoSave.saveStatus}
                                    hasEverSaved={
                                        providerNoteAutoSave.hasEverSaved || providerNoteHistoryData?.length > 0
                                    }
                                    onHistoryClick={() => {
                                        setVersionHistoryBusinessType(autoSaveNotesType);
                                        setShowHistory(true);
                                    }}
                                />
                                <Button
                                    type="text"
                                    icon={<ExpandOutlined />}
                                    onClick={() => {
                                        setShowNotes(true);
                                        setIsProviderNoteFullScreenOpen(true);
                                    }}
                                    className={s.fullScreenBtn}
                                    aria-label="Expand provider notes editor to full screen"
                                />
                            </div>
                            <span className={s.expandIcon} onClick={() => setShowNotes(!showNotes)}>
                                {showNotes ? <UpOutlined /> : <DownOutlined />}
                            </span>
                        </div>
                        {showNotes && (
                            <>
                                {/* 同上：全屏弹窗打开时避免双实例互相回写 */}
                                {isProviderNoteFullScreenOpen && (
                                    <div className={s.editorPlaceholder}>Editing in expanded view...</div>
                                )}
                                {!isProviderNoteFullScreenOpen && (
                                    <AIRichText
                                        value={myNotes}
                                        onChange={(val) => {
                                            setMyNotes(val);
                                            providerNoteAutoSave.triggerAutoSave(val);
                                        }}
                                        relationId={businessId}
                                        placeholder="Write down any notes for your own documentation..."
                                        type={
                                            isTextVisit || isStandalone
                                                ? EAIRichTextEditorType.TEXT_VISIT_NOTE_FOR_PROVIDER
                                                : EAIRichTextEditorType.AI_EDITOR_APPOINTMENT_NOTE_FOR_PROVIDER
                                        }
                                    />
                                )}
                                {onSave && (
                                    <div className={s.inlineActions}>
                                        <Button
                                            type="primary"
                                            onClick={() => {
                                                setSavingSection('myNotes');
                                                onSave();
                                            }}
                                            loading={saving && savingSection === 'myNotes'}
                                        >
                                            Save
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            <div className={s.section}>
                <BillingNote
                    value={billingNote}
                    onChange={setBillingNote}
                    businessId={businessId}
                    isTextVisit={isTextVisit || isStandalone}
                    footer={renderSectionSaveFooter('billingNote')}
                />
            </div>

            <div className={s.section}>
                <DiagnosisSection
                    value={diagnosisEntries}
                    onChange={setDiagnosisEntries}
                    specialty={
                        isTextVisit || isStandalone
                            ? (caseData as TextVisitCase)?.specialty
                            : (caseData as IVideoTreatmentDetail)?.serviceSpecialty
                    }
                    bordered={!!onSave}
                    footer={renderSectionSaveFooter('diagnosis')}
                />
            </div>

            <div className={s.section}>
                <BillingCodesSection
                    value={billingCodeEntries}
                    onChange={setBillingCodeEntries}
                    specialty={
                        isTextVisit || isStandalone
                            ? (caseData as TextVisitCase)?.specialty
                            : (caseData as IVideoTreatmentDetail)?.serviceSpecialty
                    }
                    bordered={!!onSave}
                    footer={renderSectionSaveFooter('billingCodes')}
                />
            </div>

            {/* {isTextVisit && !isRefill && ( */}
            {(isTextVisit || isStandalone) && (
                <div className={s.section}>
                    <div className={s.sectionHeader}>
                        <h3 className={s.sectionTitle}>Schedule auto follow-ups</h3>
                        <p className={s.sectionSubtitle}>
                            Automatically sends a follow-up message to patients after their visit, ensuring they feel
                            cared for and supported beyond the appointment.
                        </p>
                    </div>

                    <div className={s.sectionContent}>
                        <div className={s.followUpSettings}>
                            <Checkbox
                                checked={autoFollowUpEnabled}
                                onChange={(e) => setAutoFollowUpEnabled(e.target.checked)}
                                className={s.followUpCheckbox}
                            >
                                <span className={s.title}>Send auto follow-up reminder</span>
                            </Checkbox>

                            {autoFollowUpEnabled && (
                                <div className={s.followUpDays}>
                                    <InputNumber
                                        value={followUpNumber}
                                        onChange={(val) => setFollowUpNumber(val ?? 21)}
                                        className={s.daysInput}
                                        min={0}
                                        precision={0}
                                        controls={false}
                                        style={{ width: '90px' }}
                                    />
                                    <Select
                                        value={followUpUnit}
                                        onChange={setFollowUpUnit}
                                        dropdownStyle={{ minWidth: '100px' }}
                                        className={s.timeUnitSelect}
                                    >
                                        <Select.Option value="D">Days</Select.Option>
                                        <Select.Option value="W">Weeks</Select.Option>
                                        <Select.Option value="M">Months</Select.Option>
                                    </Select>
                                </div>
                            )}
                        </div>

                        {autoFollowUpEnabled && (
                            <div className={s.messagesSection}>
                                <div className={s.messageHeader}>
                                    <span className={s.messageTitle}>Messages</span>
                                </div>
                                <AiTextEditor
                                    type={EAIEditorType.TEXT_VISIT_REMINDER_MESSAGE}
                                    value={followUpMessage}
                                    onChange={(val) => setFollowUpMessage(val as string)}
                                    rows={8}
                                    placeholder="Hi [Patient First Name], It's been a few weeks since your last visit..."
                                    hideWriteLike
                                    relationId={businessId}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showUpcomingVisits && inInMeetingWhitelist && (
                <div className={s.section}>
                    <div className={s.sectionHeader}>
                        <h3 className={s.sectionTitle}>Upcoming visit</h3>
                        <p className={s.sectionSubtitle}>
                            The patient’s upcoming visit is scheduled. Feel free to update the time anytime.
                        </p>
                    </div>
                    {displayUpcomingVisits.length > 0 ? (
                        <div className={s.upcomingVisits}>
                            {displayUpcomingVisits.map((visit) => (
                                <VisitCard
                                    key={visit.id}
                                    visit={visit}
                                    onReschedule={() => {
                                        setScheduledVisit(visit);
                                        setShowRescheduleModal(true);
                                    }}
                                />
                            ))}
                        </div>
                    ) : (
                        <Button
                            className="max-w-fit"
                            onClick={() => {
                                setShowFollowUpModal(true);
                            }}
                        >
                            Schedule a follow-up
                        </Button>
                    )}
                </div>
            )}

            <Modal
                open={isTreatmentPlanFullScreenOpen}
                onCancel={() => setIsTreatmentPlanFullScreenOpen(false)}
                footer={null}
                title="Visit Notes (Patient-facing)"
                width="92vw"
                zIndex={10001}
                style={{ top: 20, padding: 0 }}
                bodyStyle={{ maxHeight: '80vh', padding: 0, overflow: 'auto' }}
                wrapClassName={s.fullScreenEditorModalWrap}
            >
                <div className={s.fullScreenEditorContent}>
                    <div className={s.diagnosisTitleRow}>
                        <span className={s.title}>Diagnosis & Visit Notes</span>
                        <SaveStatusIndicator
                            status={treatmentPlanAutoSave.saveStatus}
                            hasEverSaved={treatmentPlanAutoSave.hasEverSaved || treatmentPlanHistoryData?.length > 0}
                            onHistoryClick={() => {
                                setVersionHistoryBusinessType(autoSaveTreatmentPlanType);
                                setShowHistory(true);
                            }}
                        />
                        {true && (
                            <div className={s.aiDraftBadge}>
                                <span className={s.aiIcon}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M7.47969 11.4796L9.02563 6.8418H9.97431L11.5203 11.4796L16.1581 13.0256V13.9743L11.5203 15.5202L9.97431 20.158H9.02563L7.47969 15.5202L2.84186 13.9743V13.0256L7.47969 11.4796Z"
                                            fill="url(#paint0_linear_507_21172)"
                                        />
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M16.2297 5.22963L17.0256 2.8418H17.9743L18.7703 5.22963L21.1581 6.02557V6.97425L18.7703 7.7702L17.9743 10.158H17.0256L16.2297 7.7702L13.8419 6.97425V6.02557L16.2297 5.22963Z"
                                            fill="url(#paint1_linear_507_21172)"
                                        />
                                        <defs>
                                            <linearGradient
                                                id="paint0_linear_507_21172"
                                                x1="12"
                                                y1="2.8418"
                                                x2="12"
                                                y2="20.158"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stopColor="#0E62F4" />
                                                <stop offset="1" stopColor="#BB5BFF" />
                                            </linearGradient>
                                            <linearGradient
                                                id="paint1_linear_507_21172"
                                                x1="12"
                                                y1="2.8418"
                                                x2="12"
                                                y2="20.158"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stopColor="#0E62F4" />
                                                <stop offset="1" stopColor="#BB5BFF" />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                </span>
                                <span>AI-Assisted Draft</span>
                                <Switch
                                    checked={isTreatmentPlanAiDraft}
                                    onChange={setIsTreatmentPlanAiDraft}
                                    disabled={visitNotesSubmitted}
                                />
                            </div>
                        )}
                    </div>

                    <TreatmentTemplateSelection
                        specialty={
                            isTextVisit || isStandalone
                                ? (caseData as TextVisitCase)?.specialty
                                : (caseData as IVideoTreatmentDetail)?.serviceSpecialty
                        }
                        visitType={(caseData as IVideoTreatmentDetail)?.serviceType}
                        isStandalone={isStandalone}
                        needInitContent={false}
                        value={selectedTemplateId}
                        onChange={handleApplyTemplate}
                        disabled={visitNotesSubmitted}
                    />

                    <AIRichText
                        loading={aPlanLoading}
                        value={treatmentPlanValue}
                        onChange={(val) => {
                            setTreatmentPlanValue(val);
                            treatmentPlanAutoSave.triggerAutoSave(val);
                        }}
                        relationId={businessId}
                        placeholder="Include your clinical assessment, diagnosis, and recommended visit notes..."
                        height="calc(80vh - 200px)"
                        type={
                            isTextVisit || isStandalone
                                ? EAIRichTextEditorType.TEXT_VISIT_TREATMENT
                                : EAIRichTextEditorType.AI_EDITOR_APPOINTMENT_TREATMENT
                        }
                        disabled={visitNotesSubmitted}
                    />

                    <AttachFiles
                        value={attachFiles}
                        onFilesChange={handleSaveAttachments}
                        accept="image/*,application/pdf"
                    />
                </div>
            </Modal>

            <Modal
                open={isProviderNoteFullScreenOpen}
                onCancel={() => setIsProviderNoteFullScreenOpen(false)}
                footer={null}
                title="My own notes"
                width="92vw"
                zIndex={10001}
                style={{ top: 20, padding: 0 }}
                bodyStyle={{ maxHeight: '80vh', padding: 0, overflow: 'auto' }}
                wrapClassName={s.fullScreenEditorModalWrap}
            >
                <div className={s.fullScreenEditorContent}>
                    <div className={s.diagnosisTitleRow}>
                        <span className={s.title}>My own notes</span>
                        <SaveStatusIndicator
                            status={providerNoteAutoSave.saveStatus}
                            hasEverSaved={providerNoteAutoSave.hasEverSaved || providerNoteHistoryData?.length > 0}
                            onHistoryClick={() => {
                                setVersionHistoryBusinessType(autoSaveNotesType);
                                setShowHistory(true);
                            }}
                        />
                    </div>

                    <AIRichText
                        value={myNotes}
                        onChange={(val) => {
                            setMyNotes(val);
                            providerNoteAutoSave.triggerAutoSave(val);
                        }}
                        relationId={businessId}
                        placeholder="Write down any notes for your own documentation..."
                        type={
                            isTextVisit || isStandalone
                                ? EAIRichTextEditorType.TEXT_VISIT_NOTE_FOR_PROVIDER
                                : EAIRichTextEditorType.AI_EDITOR_APPOINTMENT_NOTE_FOR_PROVIDER
                        }
                        height="calc(80vh - 160px)"
                    />
                </div>
            </Modal>

            <VersionHistoryDrawer
                open={showHistory}
                onClose={() => {
                    setShowHistory(false);
                    setVersionHistoryBusinessType(undefined);
                }}
                businessId={businessId}
                businessType={versionHistoryBusinessType}
                onRestore={(content) => {
                    // 根据businessType恢复不同的字段
                    if (
                        versionHistoryBusinessType === EAutoSaveBusinessType.TEXT_VISIT_TREATMENT_PLAN ||
                        versionHistoryBusinessType === EAutoSaveBusinessType.APPOINTMENT_TREATMENT_PLAN ||
                        versionHistoryBusinessType === EAutoSaveBusinessType.STANDALONE_TREATMENT_PLAN
                    ) {
                        setTreatmentPlanValue(content);
                        treatmentPlanAutoSave.triggerAutoSave(content);
                    } else if (
                        versionHistoryBusinessType === EAutoSaveBusinessType.TEXT_VISIT_NOTES ||
                        versionHistoryBusinessType === EAutoSaveBusinessType.APPOINTMENT_NOTES ||
                        versionHistoryBusinessType === EAutoSaveBusinessType.STANDALONE_NOTES
                    ) {
                        setMyNotes(content);
                        providerNoteAutoSave.triggerAutoSave(content);
                    }
                }}
            />
            <FollowUpModal
                open={showFollowUpModal}
                onCancel={() => setShowFollowUpModal(false)}
                onConfirm={() => setShowFollowUpModal(false)}
                appointmentEventDetail={caseData as IVideoTreatmentDetail}
                targetClient={caseData?.prospect!}
            />
            <RescheduleModal
                open={showRescheduleModal}
                onReopen={() => setShowRescheduleModal(true)}
                onClose={() => setShowRescheduleModal(false)}
                onSuccess={async () => {
                    setShowRescheduleModal(false);
                    setScheduledVisit(undefined);
                    refetch?.(businessId);
                }}
                appointmentEventDetail={scheduledVisit!}
                targetClient={caseData?.prospect!}
            />
        </div>
    );
});

export default Prescriptions;
